<template>
  <PanelAccordion
    :key="panelsKeys"
    :panels="panels"
  >
    <template #filter_properties>
      <FilterGlobalInformations
        v-model="commonFields"
        :form-validation="formValidationErrors"
        :is-edit="isEdit"
      />
      <div class="field-group pt-2">
        <div class="switch-row">
          <label for="grantAdditionalDelay">
            {{ t('automatedScenarios.fields.grantAdditionalDelay') }}
            <i
              v-tooltip="{ value: t('automatedScenarios.fields.grantAdditionalDelayTooltip'), class: 'tooltip-icons-outside' }"
              class="fas fa-question-circle ml-1"
            />
          </label>
          <InputSwitch
            v-model="grantAdditionalDelaySwitch"
            input-id="grantAdditionalDelay"
          />
        </div>
        <FieldErrors
          :key="componentFieldErrorsKey"
          :errors="formValidation"
          field="grant_additional_delay"
        />
      </div>

      <div
        v-if="grantAdditionalDelay === '1'"
        class="field-group pt-3"
      >
        <div>
          <div class="field-group-content">
            <div class="field-wrapper">
              <PeriodSelector
                v-model="delay"
                :units="units"
                title="automatedScenarios.fields.duration"
                custom-class-title="block mb-2 font-weight-500"
              />
              <FieldErrors
                :key="componentFieldErrorsKey"
                :errors="formValidation"
                field="extra_time"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="field-group pt-2">
        <div class="switch-row">
          <label for="isPersistent">
            {{ t('automatedScenarios.fields.persistentFilter') }}
            <i
              v-tooltip="{ value: t('automatedScenarios.fields.persistentFilterTooltip'), class: 'tooltip-icons-outside' }"
              class="fas fa-question-circle ml-1"
            />
          </label>
          <InputSwitch
            v-model="isPersistentSwitch"
            input-id="isPersistent"
          />
        </div>
      </div>
    </template>
    <template #segmentation_criteria>
      <Message
        severity="info"
        class="mt-0"
        :closable="false"
      >
        Créez des segments basés sur les caractéristiques ou les actions des contacts. Ceux qui correspondent aux critères définis seront dirigés vers la sortie appropriée du flux.
      </Message>
      <SegmentationsPanel
        v-if="segmentsConfiguration !== null"
        v-model:model-value="segmentsConfiguration"
        origin="workflow_filter_contact_segmentation"
        :show-prompt-a-i="true"
        size="small"
        :flow-data="flowData"
        :operator-step-id="operatorStepId"
      />
    </template>
  </PanelAccordion>
  <FieldErrors
    :key="componentFieldErrorsKey"
    :errors="formValidation"
    field="segmentation_config"
  />
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  inject,
  onBeforeMount,
  PropType,
  ref,
  SetupContext,
  toRaw,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Message from 'primevue/message';
import InputSwitch from 'primevue/inputswitch';
import FieldErrors from '@/components/fields/partials/FieldErrors.vue';
import { FilterGlobalInformations } from '@/components/flows/operators/shared';
import SegmentationsPanel from '@/components/segmentations/SegmentationsPanel.vue';
import { ButtonGroup, PeriodSelector, PanelAccordion, SpmPanelAccordionInterface } from '@/components/flows/operators/shared';
import type { Period } from '@/types/flow-types';
import type { FlowStructureDto } from '@/types/flow-structure';
import type { OperatorInsertContext } from '@/components/flows/operators/registry';
import type { ContactSegmentationData } from './index';
import { createDefaultData } from './index';

export default defineComponent({
  name: 'ContactSegmentationForm',

  components: {
    PanelAccordion,
    Message,
    InputSwitch,
    ButtonGroup,
    PeriodSelector,
    FieldErrors,
    FilterGlobalInformations,
    SegmentationsPanel,
  },

  props: {
    modelValue: {
      type: Object as PropType<ContactSegmentationData>,
      required: true,
      default() {
        return createDefaultData();
      },
    },

    formValidation: {
      type: Object,
      required: false,
      default() {
        return {};
      },
    },

    // Step ID passed from FlowOperatorForm (for events metadata support)
    operatorId: {
      type: String,
      required: false,
      default: '',
    },
  },

  emits: ['update:modelValue'],

  setup(props: { modelValue: ContactSegmentationData; formValidation: any; operatorId: string }, context: SetupContext) {
    const { t } = useI18n();

    // Inject flow context from FlowOperatorPanel for events metadata support
    const flow = inject<{ value: FlowStructureDto | null }>('flow');
    const insertContext = inject<{ value: OperatorInsertContext | null }>('insertContext');

    // Computed values for SegmentationsPanel
    const flowData = computed(() => flow?.value ?? null);

    // For operatorStepId:
    // - Edit mode: use operatorId prop (the step ID of the step being edited)
    // - Add mode: use the first ancestor step ID (to traverse from there)
    const operatorStepId = computed(() => {
      // If we have an operatorId prop (edit mode), use it
      if (props.operatorId) {
        return props.operatorId;
      }
      // In add mode, use the first ancestor step ID from context
      // The flowEventsMetadata will traverse from there to find event triggers
      const ancestorStepIds = insertContext?.value?.allAncestorStepIds;
      if (ancestorStepIds && ancestorStepIds.length > 0) {
        return ancestorStepIds[0];
      }
      return '';
    });

    const componentFieldErrorsKey = ref(0);
    const formValidationErrors = ref(props.formValidation);

    watch(() => props.formValidation, () => {
      formValidationErrors.value = props.formValidation;
      componentFieldErrorsKey.value += 1;
    });

    // Determine if we are editing an existing operator
    const isEdit = ref(!!props.modelValue.custom_title);

    const panelsKeys = ref(0);
    const panels: SpmPanelAccordionInterface[] = [
      {
        id: 'filter_properties',
        title: 'Propriétés du filtre',
      },
      {
        id: 'segmentation_criteria',
        title: 'Critères de segmentation',
      },
    ];

    const commonFields = ref({
      custom_title: props.modelValue.custom_title,
      custom_description: props.modelValue.custom_description,
      outputs_number: props.modelValue.outputs_number,
      initial_number_outputs: props.modelValue.initial_number_outputs,
    });

    const grantAdditionalDelay = ref(props.modelValue.grant_additional_delay);
    const grantAdditionalDelaySwitch = computed({
      get: () => grantAdditionalDelay.value === '1',
      set: (val: boolean) => { grantAdditionalDelay.value = val ? '1' : '0'; },
    });
    const delay = ref<Period>({
      unit: props.modelValue.extra_time?.unit ?? 'DAY',
      value: props.modelValue.extra_time?.value ?? 1,
    });

    const units = [
      { value: 'SECOND', label: t('automatedScenarios.fields.durations.second(s)') },
      { value: 'MINUTE', label: t('automatedScenarios.fields.durations.minute(s)') },
      { value: 'HOUR', label: t('automatedScenarios.fields.durations.hour(s)') },
      { value: 'DAY', label: t('automatedScenarios.fields.durations.day(s)') },
    ];

    const isPersistent = ref(props.modelValue.is_persistent ?? '0');
    const isPersistentSwitch = computed({
      get: () => isPersistent.value === '1',
      set: (val: boolean) => { isPersistent.value = val ? '1' : '0'; },
    });

    const segmentsConfiguration = ref<any>(null);

    const update = () => {
      context.emit('update:modelValue', createDefaultData({
        ...commonFields.value,
        grant_additional_delay: grantAdditionalDelay.value,
        extra_time: delay.value,
        segmentation_config: segmentsConfiguration.value,
        is_persistent: isPersistent.value,
      }));
    };

    watch([commonFields, grantAdditionalDelay, delay, segmentsConfiguration, isPersistent], update, { deep: true });

    onBeforeMount(() => {
      if (props.modelValue.segmentation_config) {
        segmentsConfiguration.value = toRaw(props.modelValue.segmentation_config);
      }
      update();
    });

    return {
      t,
      componentFieldErrorsKey,
      formValidationErrors,
      isEdit,
      panelsKeys,
      panels,
      commonFields,
      grantAdditionalDelay,
      grantAdditionalDelaySwitch,
      delay,
      units,
      isPersistentSwitch,
      segmentsConfiguration,
      flowData,
      operatorStepId,
    };
  },
});
</script>

<style scoped>
.switch-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.6rem 0;
}

.switch-row label {
  margin: 0;
  font-weight: 500;
  color: #495057;
}

.switch-row :deep(.p-inputswitch) {
  flex-shrink: 0;
}
</style>
